import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import CheckoutSectionContent from './utils/Checkout.SectionContent';
import CheckoutSectionFooter from './utils/Checkout.SectionFooter';
import { translate } from '../../Services/translation';
import {
  setCustomerDetails,
  setAlternativeAddress,
  setShowAlternativeAddress,
  setSignUp,
  setValidationErrors,
} from '../../Actions/Checkout.action';
import { privateCustomerAddressSchema } from './utils/Checkout.ValidationSchema';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import Dropdown from '../Dropdown';
import Checkbox from '../Inputs/Checkbox';
import Input from '../Inputs/Input';
import { InputProps } from '../Inputs/types/Input.types';
import clsx from 'clsx';
import CheckoutSection from './utils/Checkout.Section';
import { CustomerInfoInput } from './Checkout.CustomerInfoInput';

const PrivateCustomerInfo = ({ showSignUp }: { showSignUp: boolean }) => {
  const dispatch = useDispatch();
  const { payload } = useTypedSelector((state) => state.checkout);
  const {
    authenticated,
    customerDetails = {},
    signUp,
    loginUrl,
    showAlternativeAddress,
  } = payload;
  const classes: any = {};

  const onCustomerDetailsChange = useCallback(
    (stateKey, id, value) => {
      switch (stateKey) {
        case 'customerDetails':
          dispatch(setCustomerDetails(id, value));
          break;
        case 'alternativeAddress':
          dispatch(setAlternativeAddress(id, value));
          break;
      }
    },
    [dispatch]
  );

  const titleRight = useMemo(
    () =>
      !authenticated ? (
        <div className="text-md">
          {translate('checkout.customerinfo.existingcustomer')}{' '}
          <a
            href={loginUrl}
            className={clsx('text-gray-900', 'underline')}
          >
            {translate('checkout.customerinfo.login')}
          </a>
        </div>
      ) : undefined,
    [authenticated, loginUrl]
  );

  return (
    <CheckoutSection
      title={translate('checkout.customerinfo.title')}
      titleRight={titleRight}
    >
      <CheckoutSectionContent>
        <form className={clsx('grid', 'gap-6', 'grid-cols-2')}>
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="customerDetails"
            id="firstName"
            autoComplete="given-name"
            name="fname"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="customerDetails"
            id="lastName"
            autoComplete="family-name"
            name="lname"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey="customerDetails"
            id="address"
            autoComplete="address-line1"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey="customerDetails"
            id="careOf"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="customerDetails"
            id="zipCode"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="customerDetails"
            id="city"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <div className="col-span-2">
            <label className="form__label" htmlFor="country">
              <b>{translate('checkout.customerinfo.country')}</b>
            </label>
            <Dropdown
              selectedValue={customerDetails.country}
              options={global['LitiumConstants'].countries.map(
                ({ text, value }) => ({ label: text, value })
              )}
              onChange={(value) => {
                onCustomerDetailsChange(
                  'alternativeAddress',
                  'country',
                  value
                );
                onCustomerDetailsChange(
                  'customerDetails',
                  'country',
                  value
                );
              }}
            />
          </div>
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="customerDetails"
            id="phoneNumber"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="customerDetails"
            id="email"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
        </form>
      </CheckoutSectionContent>
      <CheckoutSectionFooter className={classes.sectionFooter}>
        {showSignUp && (
          <Checkbox
            wrapperClassName={clsx(
              'flex',
              'flex-1',
              'mt-6',
              '[&:not[:first-child]]:mb-6'
            )}
            id="signupandlogin"
            inputSize="large"
            checked={signUp}
            label={translate('checkout.customerinfo.signupandlogin')}
            onChange={(e) => dispatch(setSignUp(e.target.checked))}
          />
        )}
        <Checkbox
          wrapperClassName={clsx(
            'flex',
            'flex-1',
            'mt-6',
            '[&:not[:first-child]]:mb-6'
          )}
          id="showAlternativeAddress"
          inputSize="large"
          checked={showAlternativeAddress}
          label={translate('checkout.customerinfo.showAlternativeAddress')}
          onChange={(e) =>
            dispatch(setShowAlternativeAddress(e.target.checked))
          }
        />
      </CheckoutSectionFooter>
      {showAlternativeAddress && (
        <CheckoutSectionContent>
          <form className="section-content">
            <CustomerInfoInput
              wrapperClassName="col-span-1"
              stateKey="alternativeAddress"
              id="firstName"
              autoComplete="given-name"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="col-span-1"
              stateKey="alternativeAddress"
              id="lastName"
              autoComplete="family-name"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="col-span-2"
              stateKey="alternativeAddress"
              id="address"
              autoComplete="address-line1"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="col-span-2"
              stateKey="alternativeAddress"
              id="careOf"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="col-span-1"
              stateKey="alternativeAddress"
              id="zipCode"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="col-span-1"
              stateKey="alternativeAddress"
              id="city"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
          </form>
        </CheckoutSectionContent>
      )}
    </CheckoutSection>
  );
};

export default PrivateCustomerInfo;
